
import { AuthorizedUser } from "@/trust-atcs-app/models/authorized-user";
import store from "@/trust-atcs-app/store";
import { Inject, Options, Vue, Watch } from "vue-property-decorator";
import debounce from 'lodash/debounce';
import AuthenticationService from '../../modules/authentication/authentication.service';
import { AxiosInstance } from "axios";
import { RouteN } from "@/trust-atcs-app/constants/router-name";
import LoadingSpinner from "@/shared/components/loading-spinner/index.vue";
import { TrustAPI } from "@/shared/modules/trust-api";
import { LawIdUser, LawIdUserRequest } from "@/shared/modules/typescript-trustapi-client/models";
import DatePicker from 'vue3-datepicker';
import { AppInsights } from "@/trust-atcs-app/appInsights";
import { EventEmitter } from "events";
import FormatDateTimeMixin from '@/shared/mixins/format-datetime';

@Options({
  components: {
    LoadingSpinner,
    DatePicker,
  },
  mixins: [FormatDateTimeMixin],
})
export default class AtcsImpersonate extends Vue {

  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  private authService?: AuthenticationService;
  private authorizedUser: AuthorizedUser = new AuthorizedUser;
  private authApiAxios?: AxiosInstance;

  private ai = new AppInsights;
  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }

  private isOpen = false;
  private arrowCounter = -1;
  private search = '';
  private isSearching = false;
  private srchDebounced: any = null;
  private currentSelectedLawId: LawIdUser = null as any;
  private adjustedDate = null as any;

  private practitionerList: LawIdUser[] = [];

  private impersonatingUser = false;

  @Watch('search')
  private onKeywordChange(): void {
    if (this.search.length > 2 && this.practitionerList.filter(x => x.lawId == this.search).length == 0) {
      this.currentSelectedLawId = null as any;
      setTimeout(this.srchDebounced.cancel, 0);
      setTimeout(this.srchDebounced, 0);
    }
  }

  mounted(): void {
    window.addEventListener('click', this.handleClickOutside);
    this.getSystemDate();
    this.srchDebounced = debounce(() => {
      this.searchPractitioner();
    }, 1000);
  }

  beforeUnmount(): void {
    window.removeEventListener('click', this.handleClickOutside);
  }

  private async searchPractitioner(): Promise<void> {
    const _body: LawIdUserRequest = {
      lawId: this.search
    }
    try {
      this.isSearching = true;

      const resp: any = await this.apiService.LawIdApi?.apiV2LawIdGetLawIdUsersPost(_body);
      this.practitionerList = resp.data.data as LawIdUser[];
      this.isOpen = true;
    } catch (err) {
      this.isOpen = false;
      this.ai.trackException('Search Practitioner error: ' + err);
    } finally {
      this.isSearching = false;
    }
  }

  private selectPractitioner(p: LawIdUser): void {
    this.isOpen = false;
    this.search = p.lawId;
    this.currentSelectedLawId = p;
  }

  private onArrowDown(evt: any) {
    if (this.arrowCounter < this.practitionerList.length) {
      this.arrowCounter = this.arrowCounter + 1;
    }
  }

  private onArrowUp(): void {
    if (this.arrowCounter > 0) {
      this.arrowCounter = this.arrowCounter - 1;
    }
  }

  private onEnter() {
    if (this.practitionerList.length > 0) {
      this.selectPractitioner(
        this.practitionerList[this.arrowCounter === -1 ? 0 : this.arrowCounter]
      );
    }
    this.isOpen = false;
  }

  private handleClickOutside(evt: any): void {
    if (this.$el === (evt.target)) {
      this.isOpen = false;
      this.arrowCounter = -1;
    }
  }

  private highlightWord(word: string): string {
    try {
      const regex = new RegExp('(' + this.search + ')', 'ig');
      return word.replace(regex, '<mark>$1</mark>');
    } catch (err) {
      return word;
    }
  }

  private formatPractitionerText(p: LawIdUser): string {
    return `${p.lawId}`;
  }

  private formatPracticesText(p: LawIdUser): string {
    if (p?.practices?.length > 0) {
      return p.practices.map((x: any) => ('- ' + x.practiceName)).join('<br />');
    } else {
      return '';
    }
  }

  private async impersonateUser(): Promise<void> {
    this.authorizedUser = store.getters.getUserAccount;
    this.authService = new AuthenticationService(this.authorizedUser, this.authApiAxios);
    this.impersonatingUser = true;

    try {
      await this.authService.checkAuthentication(this.currentSelectedLawId.lawId);
      // store.dispatch('setAuthorizedUser', this.authorizedUser);
      this.$router.push({ name: RouteN.ATCS.DashboardMain.n });
    } catch (err) {
      console.log('impersonate failed');
      this.ai.trackException('Impersonate failed: ' + err);
    } finally {
      this.impersonatingUser = false;
    }
  }

  get userAccount(): AuthorizedUser {
    return store.getters.getUserAccount;
  }

  get env(): string {
    const local = window.location.href.indexOf('localhost') > 0 ? 'DEV' : '';
    const dev = window.location.href.indexOf('-dev') > 0 ? 'DEV' : '';
    const sit = window.location.href.indexOf('-sit') > 0 ? 'SIT' : '';
    return local + dev + sit;
  }

  private getSystemDate(): void {
    this.apiService?.DateApi.apiV2DateGetBaseDateGet()
      .then((res: any) => {
        this.adjustedDate = new Date(res.data);
      })
      .catch((err: any) => {
        console.log('Get date failed: ', err);
        this.ai.trackException('Get date failed: ' + err);
      })
  }

  get minDate(): Date {
    return new Date('2010-01-01T00:00:00.000+10:00');
  }

  get maxDate(): Date {
    return new Date('2050-12-31T00:00:00.000+00:00');
  }

  private isDateToday(dateString: string) {
    // Parse the date string into a Date object
    const date = new Date(dateString);

    // Get today's date
    const today = new Date();

    // Compare years, months, and days
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  }

  get isToday(): boolean {
    return this.isDateToday((this.adjustedDate));
  }

  private setSystemDate(isReset: boolean): void {
    this.adjustedDate = isReset ? new Date() : this.adjustedDate

    const currentDate = new Date();
    const hours = currentDate.getHours();
    const minutes = currentDate.getMinutes();
    const seconds = currentDate.getSeconds();

    // Set the time components for tomorrow
    this.adjustedDate.setHours(hours);
    this.adjustedDate.setMinutes(minutes);
    this.adjustedDate.setSeconds(seconds);

    const _body = {
      dateTime: this.adjustedDate,
    }
    this.apiService?.DateApi.apiV2DateSetCurrentDatePost(_body)
      .then((response: any) => {
        this.eventCtrl.emit('show-snackbar', 'success', 'System Date set to: ' + this.$formatDate(this.adjustedDate));
      })
      .catch((err: any) => {
        this.ai.trackException('Error on set System Date: ' + err);
        this.eventCtrl.emit('show-snackbar', 'error', 'Error on set System Date: ' + this.$formatDate(this.adjustedDate));
      })
  }

}